// Admin: cross-tenant user list.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Mail, Phone, Search, UserCog } from 'lucide-react';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, EmptyState, StatusPill, DemoDataBadge } from '../../components/shared/primitives';
import { ADMIN_NAV } from '../_navs';
import { admin } from '../../lib/api';

const ROLES = [
  { value: '',          label: 'All roles' },
  { value: 'customer',  label: 'Customers' },
  { value: 'recipient', label: 'Recipients' },
  { value: 'merchant',  label: 'Merchants' },
  { value: 'admin',     label: 'Admins' },
];

export function AdminUsersPage() {
  const [role, setRole] = useState('');
  const [q,    setQ]    = useState('');

  const usersQ = useQuery({
    queryKey: ['admin', 'users', role, q],
    queryFn: () => admin.users({ role: role || undefined, q: q.trim() || undefined }),
  });

  const counts = useMemo(() => {
    const all = usersQ.data ?? [];
    return {
      total:     all.length,
      customers: all.filter((u) => u.role === 'customer').length,
      recipients:all.filter((u) => u.role === 'recipient').length,
      merchants: all.filter((u) => u.role === 'merchant').length,
      admins:    all.filter((u) => u.role === 'admin' || u.role === 'super_admin').length,
    };
  }, [usersQ.data]);

  return (
    <PortalShell nav={ADMIN_NAV} activeId="users" title="Users" subtitle="All accounts across roles + tenants.">
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[12px]">
          {[
            ['Total', counts.total],
            ['Customers', counts.customers],
            ['Recipients', counts.recipients],
            ['Merchants', counts.merchants],
            ['Admins', counts.admins],
          ].map(([label, n]) => (
            <Card key={String(label)} padding="p-3" className="text-center">
              <p className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">{label}</p>
              <p className="text-[18px] font-semibold text-slate-900 mt-0.5">{n}</p>
            </Card>
          ))}
        </div>

        <Card padding="p-3" className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Email or phone…" className="w-full pl-9 pr-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
          </div>
          <select value={role} onChange={(e) => setRole(e.target.value)} className="text-[12.5px] py-2 px-2 rounded-md border border-slate-200 bg-white">
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </Card>

        <Card padding="p-0">
          {usersQ.isLoading ? (
            <div className="text-center py-12 text-[13px] text-slate-400"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div>
          ) : (usersQ.data?.length ?? 0) === 0 ? (
            <EmptyState icon={UserCog} title="No users match" body="Try clearing the search or role filter." />
          ) : (
            <table className="text-[13px] w-full block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
              <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
                <tr>
                  <th className="text-left py-2.5 px-5 font-medium">User</th>
                  <th className="text-left py-2.5 px-3 font-medium">Role</th>
                  <th className="text-left py-2.5 px-3 font-medium">Status</th>
                  <th className="text-left py-2.5 px-3 font-medium">Verified</th>
                  <th className="text-left py-2.5 px-3 font-medium">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(usersQ.data ?? []).map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="py-3 px-5">
                      {u.email && <p className="inline-flex items-center gap-1 text-slate-900 text-[12.5px]"><Mail className="w-3 h-3 text-slate-400" /> {u.email}</p>}
                      {u.phone_e164 && <p className="inline-flex items-center gap-1 text-[11.5px] text-slate-500"><Phone className="w-3 h-3" /> {u.phone_e164}</p>}
                      <p className="text-[10.5px] text-slate-400 font-mono mt-0.5">id #{u.id} · {u.locale}</p>
                    </td>
                    <td className="py-3 px-3"><span className="text-[12px] font-semibold capitalize text-slate-700">{u.role.replace('_', ' ')}</span></td>
                    <td className="py-3 px-3"><StatusPill status={u.status} /></td>
                    <td className="py-3 px-3 text-[11.5px] text-slate-600">
                      <div className="flex flex-col gap-0.5">
                        <span>{u.email_verified_at ? '✓ Email' : '— Email'}</span>
                        <span>{u.phone_verified_at ? '✓ Phone' : '— Phone'}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-[12px] text-slate-600">{new Date(u.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <p className="text-[11.5px] text-slate-400 inline-flex items-center gap-2">
          <DemoDataBadge note="impersonation + per-user audit + role changes ship with Phase 3 admin ops" />
        </p>
      </div>
    </PortalShell>
  );
}
